<template>
  <div class="videoHeaderContent">
    <!-- Title with input -->
    <div class="header-block header-left">
      <video-header-title
        class="header-title"
        :title="title"
        @updateTitle="$emit('updateTitle', $event)"
        @updateTitleStart="$emit('updateTitleStart')"
      />
    </div>

    <div v-if="isVideoProcessing" class="header-block header-middle">
      <div class="progress-bar-container">
        <el-progress
          :status="'success'"
          :width="32"
          :text-inside="true"
          :percentage="progress"
          :stroke-width="32"
          :stroke-linecap="'square'"
          class="videoProgressBar"
        />
      </div>
    </div>

    <div v-if="percentCompleted >0 && percentCompleted <=100 && isUploadingProssesing" class="header-block header-middle">
      <div class="progress-bar-container">
        <el-progress
          :status="'success'"
          :width="32"
          :text-inside="true"
          :percentage="percentCompleted"
          :stroke-width="32"
          :stroke-linecap="'square'"
          class="videoProgressBar"
        />
      </div>
    </div>

    <div
      v-if="areSharings"
      ref="middleBlock"
      class="header-block header-middle"
    >
      <!-- Share video status icons -->
      <div>
        <sharing-statuses-scroller
          class="sharings-scroller"
          :sharings="sharings"
          :is-uploaded-to-mangomolo="isUploadedToMangomolo"
          :is-uploading-to-mangomolo="isUploadingToMangomolo"
          :length="sharingsScrollLength"
          :icon-size="scrollSizes.itemSize"
          :scroll-padding="scrollSizes.padding"
          :scroller-width="scrollSizes.scrollerWidth"
          :timezone="timezone"
          @sharingRemoved="$emit('sharingRemoved', $event)"
        />
      </div>
    </div>

    <div class="header-block header-right">
      <!-- Converting statuses -->
      <video-header-status :status="status" :progress="progress" />

      <!-- Video actions -->
      <video-header-actions
        :is-ready="isVideoReady"
        :is-removable="isRemovable"
        @edit="$emit('edit')"
        @gif="$emit('gif')"
        @cut="$emit('cut')"
        @preview="$emit('preview')"
        @download="$emit('download')"
        @copy="$emit('copy')"
        @cloneVideo="$emit('cloneVideo')"
        @delete="$emit('delete')"
      />
    </div>
  </div>
</template>

<script>
import VideoHeaderTitle from "@/components/media/video/VideoHeaderTitle.vue";
import SharingStatusesScroller from "@/components/socials/share/SharingStatusesScroller.vue";
import VideoHeaderStatus from "@/components/media/video/VideoHeaderStatus.vue";
import VideoHeaderActions from "@/components/media/video/VideoHeaderActions.vue";

import { mapState } from "vuex";

import { getBounded } from "@/utils/calc.js";

import { MEDIA_CONVERSION_STATUS, isMediaStatusValid } from "@/constants.js";

// @group Media/Video
// Video item card's header
export default {
  name: "VideoItemHeader",
  components: {
    VideoHeaderTitle,
    SharingStatusesScroller,
    VideoHeaderStatus,
    VideoHeaderActions
  },
  model: {
    prop: "title",
    event: "updateTitle"
  },
  props: {
    /** Audio title */
    title: {
      type: String,
      default: ""
    },
    /** Audio conversion status */
    status: {
      type: String,
      default: MEDIA_CONVERSION_STATUS.READY,
      validator(val) {
        return isMediaStatusValid(val);
      }
    },
    /** Conversion progress percentage */
    progress: {
      type: Number,
      default: 0,
      validator(val) {
        return 0 <= val && val <= 100;
      }
    },
    percentCompleted: {
      type: Number,
      default: 0,
      validator(val) {
        return 0 <= val && val <= 100;
      }
    },
    /** Is it possible to delete audio */
    isRemovable: {
      type: Boolean,
      default: true
    },
    /** clip sharings to socials */
    sharings: {
      type: Array,
      default: () => []
    },
    /** is clip uploaded to MM */
    isUploadedToMangomolo: {
      type: Boolean,
      default: false
    },
    /** is clip uploading to MM now */
    isUploadingToMangomolo: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      /** current scroll sizes */
      scrollSizes: {
        padding: 3,
        itemSize: 26,
        scrollerWidth: 16
      },
      /** scroll sizes for different resolutions */
      scrollAdaptive: {
        mobile: {
          padding: 3,
          itemSize: 32,
          scrollerWidth: 24
        },
        desktop: {
          padding: 3,
          itemSize: 26,
          scrollerWidth: 16
        }
      },
      /** size when use mobile layout */
      mobileLimit: 991,
      sharingsScrollLength: 5
    };
  },
  /** scroll length option */
  scrollParams: {
    maxLength: 7,
    minLength: 3
  },
  computed: {
    ...mapState({
      timezone: state => state.timezone.timezone
    }),
    // @vuese
    // Is video converted
    isVideoReady() {
      return this.status == MEDIA_CONVERSION_STATUS.READY || this.status == MEDIA_CONVERSION_STATUS.REMOTE ;
    },
    // @vuese
    // Is video converting now
    isVideoProcessing() {
      const {
        CONVERTING: converting,
        POSTPROCESSING: postprocessing
      } = MEDIA_CONVERSION_STATUS;

      return [converting, postprocessing].includes(this.status);
    },
    isUploadingProssesing() {
      return this.status == MEDIA_CONVERSION_STATUS.ENQUEUED
    },
    // @vuese
    // Minimum scroll panel width
    minScrollWidth() {
      const { padding, itemSize, scrollerWidth } = this.scrollSizes;
      return (padding + scrollerWidth) * 2 + itemSize;
    },
    // @vuese
    // Are any sharing here
    areSharings() {
      return (
        this.sharings.length > 0 ||
        this.isUploadedToMangomolo ||
        this.isUploadingToMangomolo
      );
    }
  },
  watch: {
    // @vuese
    // When sharings (dis)appears, then update sharing scroll length
    areSharings() {
      this.$nextTick(this.updateSharingsScrollLength);
    }
  },
  mounted() {
    this.updateSharingsScroll();
    window.addEventListener("resize", this.updateSharingsScroll);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateSharingsScroll);
  },
  methods: {
    // @vuese
    // Get actual scroll length in pixels
    getSharingsScrollLength() {
      const availableWidth = this.$refs.middleBlock.clientWidth;
      const { padding, itemSize, scrollerWidth } = this.scrollSizes;
      const { maxLength, minLength } = this.$options.scrollParams;

      const reservedWidth = (padding + scrollerWidth) * 2;
      const freeWidth = availableWidth - reservedWidth;

      const targetLength = getBounded(
        this.sharings.length,
        minLength,
        maxLength
      );
      const availableLength = Math.floor(freeWidth / itemSize);

      return Math.min(availableLength, targetLength);
    },
    // @vuese
    // Update scroll
    updateSharingsScroll() {
      this.updateSharingsScrollSizes();
      this.updateSharingsScrollLength();
    },
    // @vuese
    // Update scroll sizes
    updateSharingsScrollSizes() {
      if (document.body.clientWidth > this.mobileLimit) {
        this.scrollSizes = { ...this.scrollAdaptive.desktop };
      } else {
        this.scrollSizes = { ...this.scrollAdaptive.mobile };
      }
    },
    // @vuese
    // Update count of visible sharings
    updateSharingsScrollLength() {
      if (this.areSharings) {
        this.sharingsScrollLength = this.getSharingsScrollLength();
      }
    }
  }
};
</script>

<style>
.videoProgressBar .el-progress-bar__innerText {
  color: white;
}
.videoProgressBar .el-progress-bar .el-progress-bar__inner {
  text-align: center;
  height: 2rem;
}
.videoProgressBar .el-progress-bar .el-progress-bar__outer {
  height: 2rem;
}
</style>

<style lang="scss" scoped>
/** header */
.videoHeaderContent {
  width: 100%;
  padding: 10px;
  flex-wrap: wrap;

  box-sizing: border-box;
  display: flex;
}
.header-right {
  flex-grow: 1;
}
.actionGroup {
  display: flex;
  justify-content: flex-end;
  vertical-align: middle;
}

/** Title */
.videoTitleContainer {
  display: flex;
  justify-content: flex-start;
  vertical-align: middle;
  margin-top: 7px;
}

.header-left {
  align-self: center;
  margin-bottom: 5px;
}
.header-middle {
  display: flex;
  justify-content: center;
  margin: 5px;
}
.header-right {
  display: flex;
}

.header-title {
  align-self: center;
  width: fit-content;
  margin: auto;
}
.sharings-scroller {
  width: 100%;
}

/** progress bar */
.videoProgressBar {
  color: white;
  width: 100%;
  max-width: 200px;
  height: 2rem;
  margin: 1px;
  border-radius: 5px !important;
}
.progress-bar-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

@media only screen and (max-width: 991px) {
  .header-middle {
    width: 100%;
    justify-content: center;
  }

  .header-right {
    justify-content: flex-end;
  }
}

@media only screen and (min-width: 992px) {
  .header-right {
    justify-content: flex-end;
    margin-top: 5px;
  }
}

@media only screen and (min-width: 1200px) {
}
@media only screen and (min-width: 1550px) {
  .videoHeaderContent {
    flex-direction: row;
    justify-content: space-between;
    padding: 10px;
  }
  .header-block {
    margin: auto;
  }
  .header-left {
    width: 100px;
    flex-grow: 1;
  }
  .header-middle {
    flex-grow: 1;
    width: 0;
    justify-content: flex-start;
  }
  .header-right {
    margin-left: 5px;
    flex-grow: 0;
    justify-content: flex-end;
  }
  .header-title {
    width: 100%;
  }
  /** editable title */
  .videoTitleContainer {
    margin-top: 8px;
  }
}
@media only screen and (min-width: 1920px) {
  /** editable title */
  .videoTitleContainer {
    margin-top: 7px;
  }
  /** progress bar */
  .videoProgressBar {
    height: 2.2rem;
    margin: 3px;
  }
  .el-progress-bar__inner {
    border-radius: 5px !important;
    height: 2.2rem;
  }
  .el-progress-bar__outer {
    border-radius: 5px !important;
    height: 2.2rem;
  }
}
</style>
